import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BatteryChargingIcon, SaveIcon, XIcon, EditIcon, Trash2Icon, PlusIcon, PackageIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { collection, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
interface ChargerPrices {
  'Zaptec 1': number;
  'Zaptec 2': number;
  Andet: number;
}
interface ChargerSettings {
  prices: ChargerPrices;
  averageSalePrice?: number;
}
interface ChargerMaterial {
  name: string;
  price: number;
  quantity: number;
}
export interface StandardMaterial {
  id: string;
  name: string;
  price?: number;
}
const defaultChargerMaterials: ChargerMaterial[] = [{
  name: 'Kombiafbr.',
  price: 465.11,
  quantity: 1
}, {
  name: 'Ladeboks',
  price: 1600.0,
  quantity: 1
}, {
  name: 'Kabel 5g4',
  price: 432.16,
  quantity: 20
}, {
  name: 'Tavle UG 150',
  price: 71.43,
  quantity: 1
}, {
  name: 'Diverse små ting',
  price: 240.0,
  quantity: 1
}, {
  name: 'ELCON Timeløn',
  price: 545.0,
  quantity: 1
}];
export function ChargerSettings() {
  const [prices, setPrices] = useState<ChargerPrices>({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  const [averageSalePrice, setAverageSalePrice] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMaterials, setIsEditingMaterials] = useState(false);
  const [tempPrices, setTempPrices] = useState<ChargerPrices>(prices);
  const [tempAverageSalePrice, setTempAverageSalePrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Standard Materials state
  const [standardMaterials, setStandardMaterials] = useState<StandardMaterial[]>([]);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newMaterialPrice, setNewMaterialPrice] = useState('');
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editMaterialName, setEditMaterialName] = useState('');
  const [editMaterialPrice, setEditMaterialPrice] = useState('');
  // Materials state
  const [chargerMaterials, setChargerMaterials] = useState<ChargerMaterial[]>(() => {
    const saved = localStorage.getItem('chargerMaterials');
    return saved ? JSON.parse(saved) : defaultChargerMaterials;
  });
  const [installationPrice, setInstallationPrice] = useState(() => {
    const saved = localStorage.getItem('installationPrice');
    return saved ? parseFloat(saved) : 4400.0;
  });
  // Auto-save materials to localStorage
  useEffect(() => {
    localStorage.setItem('chargerMaterials', JSON.stringify(chargerMaterials));
  }, [chargerMaterials]);
  useEffect(() => {
    localStorage.setItem('installationPrice', installationPrice.toString());
  }, [installationPrice]);
  // Load standard materials from Firebase with real-time sync
  useEffect(() => {
    const docRef = doc(db, 'settings', 'standardMaterials');
    const unsubscribe = onSnapshot(docRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setStandardMaterials(data.items || []);
      } else {
        // Initialize if it doesn't exist
        setDoc(docRef, {
          items: []
        });
        setStandardMaterials([]);
      }
    }, error => {
      console.error('Error loading standard materials:', error);
    });
    return () => unsubscribe();
  }, []);
  // Load prices from Firebase on mount
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Support both old format (just prices) and new format (with averageSalePrice)
          if (data.prices) {
            // New format
            const loadedPrices = data.prices as ChargerPrices;
            const loadedAverage = data.averageSalePrice || 0;
            setPrices(loadedPrices);
            setAverageSalePrice(loadedAverage);
            setTempPrices(loadedPrices);
            setTempAverageSalePrice(loadedAverage);
          } else {
            // Old format - migrate to new format
            const loadedPrices = data as ChargerPrices;
            setPrices(loadedPrices);
            setAverageSalePrice(0);
            setTempPrices(loadedPrices);
            setTempAverageSalePrice(0);
          }
        } else {
          // Initialize with default prices if not exists
          const defaultPrices: ChargerPrices = {
            'Zaptec 1': 1046.3,
            'Zaptec 2': 4321.54,
            Andet: 0
          };
          await setDoc(docRef, {
            prices: defaultPrices,
            averageSalePrice: 0
          });
          setPrices(defaultPrices);
          setAverageSalePrice(0);
          setTempPrices(defaultPrices);
          setTempAverageSalePrice(0);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
        alert('Fejl ved indlæsning af priser. Tjek konsollen for detaljer.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPrices();
  }, []);
  const saveStandardMaterialsToDb = async (items: StandardMaterial[]) => {
    try {
      const docRef = doc(db, 'settings', 'standardMaterials');
      await setDoc(docRef, {
        items
      });
    } catch (error) {
      console.error('Error saving standard materials:', error);
      alert('Fejl ved gemning af standard materialer.');
    }
  };
  const handleAddStandardMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMaterialName.trim()) return;
    const newMaterial: StandardMaterial = {
      id: Date.now().toString(),
      name: newMaterialName.trim(),
      price: newMaterialPrice ? parseFloat(newMaterialPrice) : undefined
    };
    const updated = [...standardMaterials, newMaterial].sort((a, b) => a.name.localeCompare(b.name));
    await saveStandardMaterialsToDb(updated);
    setNewMaterialName('');
    setNewMaterialPrice('');
  };
  const startEditStandardMaterial = (material: StandardMaterial) => {
    setEditingMaterialId(material.id);
    setEditMaterialName(material.name);
    setEditMaterialPrice(material.price?.toString() || '');
  };
  const handleSaveStandardMaterial = async (id: string) => {
    if (!editMaterialName.trim()) return;
    const updated = standardMaterials.map(m => {
      if (m.id === id) {
        return {
          ...m,
          name: editMaterialName.trim(),
          price: editMaterialPrice ? parseFloat(editMaterialPrice) : undefined
        };
      }
      return m;
    }).sort((a, b) => a.name.localeCompare(b.name));
    await saveStandardMaterialsToDb(updated);
    setEditingMaterialId(null);
  };
  const handleDeleteStandardMaterial = async (id: string) => {
    if (confirm('Er du sikker på, at du vil slette dette materiale?')) {
      const updated = standardMaterials.filter(m => m.id !== id);
      await saveStandardMaterialsToDb(updated);
    }
  };
  const handleEdit = () => {
    setTempPrices(prices);
    setTempAverageSalePrice(averageSalePrice);
    setIsEditing(true);
  };
  const handleCancel = () => {
    setTempPrices(prices);
    setTempAverageSalePrice(averageSalePrice);
    setIsEditing(false);
  };
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const docRef = doc(db, 'settings', 'chargerPrices');
      await setDoc(docRef, {
        prices: tempPrices,
        averageSalePrice: tempAverageSalePrice
      });
      setPrices(tempPrices);
      setAverageSalePrice(tempAverageSalePrice);
      setIsEditing(false);
      // Show success message
      alert('Indstillinger gemt! Alle brugere vil nu se de opdaterede værdier.');
    } catch (error) {
      console.error('Error saving charger settings:', error);
      alert('Fejl ved gemning af indstillinger. Tjek konsollen for detaljer.');
    } finally {
      setIsSaving(false);
    }
  };
  const updatePrice = (type: keyof ChargerPrices, value: string) => {
    setTempPrices({
      ...tempPrices,
      [type]: parseFloat(value) || 0
    });
  };
  // Calculate charger profit
  const materialsCost = useMemo(() => {
    return chargerMaterials.reduce((sum, material) => sum + material.price * material.quantity, 0);
  }, [chargerMaterials]);
  const chargerProfit = installationPrice - materialsCost;
  const handleSaveMaterials = () => {
    setIsEditingMaterials(false);
  };
  const handleCancelMaterialsEdit = () => {
    const saved = localStorage.getItem('chargerMaterials');
    setChargerMaterials(saved ? JSON.parse(saved) : defaultChargerMaterials);
    const savedPrice = localStorage.getItem('installationPrice');
    setInstallationPrice(savedPrice ? parseFloat(savedPrice) : 4400.0);
    setIsEditingMaterials(false);
  };
  const updateMaterial = (index: number, field: keyof ChargerMaterial, value: string | number) => {
    const updated = [...chargerMaterials];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setChargerMaterials(updated);
  };
  if (isLoading) {
    return <div className="space-y-6" data-id="element-1117" data-mp="3boo1wukzwens">
        <header className="mb-8" data-id="element-1118" data-mp="3bppfb5phhd8o">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1119" data-mp="36x5zs378k818">
            Ladeboks information
          </h1>
          <p className="text-stone-600" data-id="element-1120" data-mp="31x1mem1mjtf8">Indlæser indstillinger...</p>
        </header>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1121" data-mp="2x01ee5l7sglh">
      <header className="mb-8" data-id="element-1122" data-mp="2wwnceyggq9ek">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1123" data-mp="2swv84xy3qpf0">
          Ladeboks information
        </h1>
        <p className="text-stone-600" data-id="element-1124" data-mp="2nxiftxogoq51">
          Administrer materialepriser og gennemsnitlige salgspriser for
          ladebokse
        </p>
      </header>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1125" data-mp="3gkyo1y2tr6ih">
        <div className="flex items-center justify-between mb-6" data-id="element-1126" data-mp="3ldi20w5fc3nt">
          <div className="flex items-center gap-3" data-id="element-1127" data-mp="3lg4aqz41wp7m">
            <div className="p-3 bg-teal-100 rounded-xl" data-id="element-1128" data-mp="2xoyu4hqadsw9">
              <BatteryChargingIcon className="w-6 h-6 text-teal-700" data-id="element-1129" data-mp="2spcgi76jqamx" />
            </div>
            <div data-id="element-1130" data-mp="2srgr17yya7w6">
              <h2 className="text-lg font-semibold text-stone-900" data-id="element-1131" data-mp="37m3fwgg7zmcj">
                Ladeboks materialepriser
              </h2>
              <p className="text-sm text-stone-500" data-id="element-1132" data-mp="32m8uutj4lqze">
                Priser bruges automatisk ved oprettelse af opgaver
              </p>
            </div>
          </div>

          {!isEditing ? <Button onClick={handleEdit} variant="primary" data-id="element-1133" data-mp="hvzy2zsrvgso">
              Rediger indstillinger
            </Button> : <div className="flex gap-2" data-id="element-1134" data-mp="pin7humgzcm0">
              <Button onClick={handleCancel} variant="secondary" className="flex items-center gap-2" disabled={isSaving} data-id="element-1135" data-mp="ug6miwqzrtf0">
                <XIcon className="w-4 h-4" data-id="element-1136" data-mp="kgxy0jewb701" />
                Annuller
              </Button>
              <Button onClick={handleSave} variant="primary" className="flex items-center gap-2" disabled={isSaving} data-id="element-1137" data-mp="186jtavs5d22r">
                <SaveIcon className="w-4 h-4" data-id="element-1138" data-mp="zf9ryiavw1ni" />
                {isSaving ? 'Gemmer...' : 'Gem ændringer'}
              </Button>
            </div>}
        </div>

        <div className="space-y-4" data-id="element-1139" data-mp="13dz11yow2x1c">
          {/* Zaptec 1 */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1140" data-mp="1r54iujee3zea">
            <div className="flex items-center justify-between" data-id="element-1141" data-mp="1vwwcx3wr8n96">
              <div className="flex-1" data-id="element-1142" data-mp="25smnjhrycu0p">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1143" data-mp="25rlefiuhyhlm">Zaptec 1</h3>
                <p className="text-sm text-stone-500" data-id="element-1144" data-mp="21qhzqzo6qrlz">Standard ladeboks type</p>
              </div>
              <div className="w-48" data-id="element-1145" data-mp="1wrf1pz6mhlni">
                {isEditing ? <Input label="Pris (kr)" type="number" step="0.01" value={tempPrices['Zaptec 1']} onChange={e => updatePrice('Zaptec 1', e.target.value)} data-id="element-1146" data-mp="1wtrk2dgaly13" /> : <div className="text-right" data-id="element-1147" data-mp="1i17llq438epz">
                    <p className="text-sm text-stone-500" data-id="element-1148" data-mp="1e2i7fvb86t5a">Materialer pris</p>
                    <p className="text-2xl font-bold text-stone-900" data-id="element-1149" data-mp="192vu7lvedcwh">
                      {prices['Zaptec 1'].toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Zaptec 2 */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1150" data-mp="txrdzg7su2ls">
            <div className="flex items-center justify-between" data-id="element-1151" data-mp="yzgu3a9vp6gq">
              <div className="flex-1" data-id="element-1152" data-mp="13x8j9wj52eh8">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1153" data-mp="13w77tqy7npyz">Zaptec 2</h3>
                <p className="text-sm text-stone-500" data-id="element-1154" data-mp="6aj9v1m3wzlc">Premium ladeboks type</p>
              </div>
              <div className="w-48" data-id="element-1155" data-mp="b81lx0vf7uxk">
                {isEditing ? <Input label="Pris (kr)" type="number" step="0.01" value={tempPrices['Zaptec 2']} onChange={e => updatePrice('Zaptec 2', e.target.value)} data-id="element-1156" data-mp="g7xpx044bmd4" /> : <div className="text-right" data-id="element-1157" data-mp="3onr7zje3l5wa">
                    <p className="text-sm text-stone-500" data-id="element-1158" data-mp="3omg3fhctvu44">Materialer pris</p>
                    <p className="text-2xl font-bold text-stone-900" data-id="element-1159" data-mp="1u6r5dtl25nc8">
                      {prices['Zaptec 2'].toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Andet */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1160" data-mp="2cwflwho5x182">
            <div className="flex items-center justify-between" data-id="element-1161" data-mp="28vdlhxiiej4i">
              <div className="flex-1" data-id="element-1162" data-mp="2mvy3irgloawy">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1163" data-mp="2muve6nyxegj5">Andet</h3>
                <p className="text-sm text-stone-500" data-id="element-1164" data-mp="2hwtuq1xl8f75">
                  Manuel indtastning af pris
                </p>
              </div>
              <div className="w-48" data-id="element-1165" data-mp="sifa9ttlrehg">
                <div className="text-right" data-id="element-1166" data-mp="nj2fmmwhp34b">
                  <p className="text-sm text-stone-500" data-id="element-1167" data-mp="nkdpn4gj373v">Materialer pris</p>
                  <p className="text-lg font-medium text-stone-600" data-id="element-1168" data-mp="2z65tzmhx5bqb">
                    Manuel indtastning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: Average Sale Price */}
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200" data-id="element-1169" data-mp="2pgcmoxot7irv">
            <div className="flex items-center justify-between" data-id="element-1170" data-mp="2ueeblz8x2cb9">
              <div className="flex-1" data-id="element-1171" data-mp="2gfeoiw11y5h7">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1172" data-mp="2ged3pzu8i4ma">
                  Gennemsnit pr ladeboks salg
                </h3>
                <p className="text-sm text-stone-500" data-id="element-1173" data-mp="26hrvz6qtyfer">
                  Gennemsnitlig salgspris for en ladeboks installation
                </p>
              </div>
              <div className="w-48" data-id="element-1174" data-mp="2bhd4o8vsy24o">
                {isEditing ? <Input label="Gennemsnitspris (kr)" type="number" step="0.01" value={tempAverageSalePrice} onChange={e => setTempAverageSalePrice(parseFloat(e.target.value) || 0)} data-id="element-1175" data-mp="4q59nihvv8om" /> : <div className="text-right" data-id="element-1176" data-mp="3ufj49fc8aw2o">
                    <p className="text-sm text-stone-500" data-id="element-1177" data-mp="3ugav6cuvs5mm">Gennemsnitspris</p>
                    <p className="text-2xl font-bold text-amber-900" data-id="element-1178" data-mp="3qepm72lgwbhp">
                      {averageSalePrice.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1179" data-mp="d5bf3grvh8bh">
          <p className="text-sm text-blue-900" data-id="element-1180" data-mp="19qv3zgrr2ewg">
            <strong data-id="element-1181" data-mp="14ytw071m7ac8">Bemærk:</strong> Når du vælger en ladeboks type ved
            oprettelse af en opgave, vil materialeprisen automatisk blive
            udfyldt med den pris du har angivet her. For "Andet" skal prisen
            indtastes manuelt.
          </p>
        </div>

        {/* Firebase sync info */}
        <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1182" data-mp="2wlqm861ua762">
          <p className="text-sm text-teal-900" data-id="element-1183" data-mp="31kuvsxsoe6wx">
            <strong data-id="element-1184" data-mp="31mo3mgf0yx6i">🔥 Firebase Sync:</strong> Alle ændringer gemmes i skyen og
            deles automatisk med alle brugere på tværs af alle computere og
            enheder.
          </p>
        </div>
      </motion.div>

      {/* Charger Profit Calculator */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm" data-id="element-1185" data-mp="3g9hacxahaun3">
        <div className="flex items-center justify-between mb-8" data-id="element-1186" data-mp="3u98vdhzexbal">
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1187" data-mp="3u7flltti6ayd">
            Gennemsnit pr ladeboks salg
          </h2>
          {!isEditingMaterials ? <button onClick={() => setIsEditingMaterials(true)} className="flex items-center gap-2 px-4 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1188" data-mp="306gvljjtuexk">
              <EditIcon className="w-4 h-4" data-id="element-1189" data-mp="3a67oxhwninw2" />
              Rediger
            </button> : <div className="flex gap-2" data-id="element-1190" data-mp="2mgn1alpjz2gg">
              <button onClick={handleCancelMaterialsEdit} className="flex items-center gap-2 px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors" data-id="element-1191" data-mp="2hhaicc1syfj5">
                <XIcon className="w-4 h-4" data-id="element-1192" data-mp="2qh6zztprye4p" />
                Annuller
              </button>
              <button onClick={handleSaveMaterials} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg transition-colors" data-id="element-1193" data-mp="22rmg98lsrcud">
                <SaveIcon className="w-4 h-4" data-id="element-1194" data-mp="1omcgry1wr33h" />
                Gem
              </button>
            </div>}
        </div>

        <div className="overflow-x-auto" data-id="element-1195" data-mp="1tendks1cq8rt">
          <table className="w-full" data-id="element-1196" data-mp="1eoojb0cx3hhm">
            <thead data-id="element-1197" data-mp="1epr6ax74czs9">
              <tr className="border-b border-stone-200" data-id="element-1198" data-mp="1eq9caljl2sdd">
                <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1199" data-mp="1jmq2dlacfgt3">
                  Materialer
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1200" data-mp="27dvd5m1ffiwj">
                  Pris
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1201" data-mp="1xf67crmt1own">
                  Stk
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1202" data-mp="22czepnzugoyi">
                  Total
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1203" data-mp="qzdcp28vysvk">
              {chargerMaterials.map((material, index) => <tr key={index} className="border-b border-stone-100" data-id="element-1204" data-mp="3mzi61nokcfum" data-mpi={index}>
                  <td className="py-3 px-4" data-id="element-1205" data-mp="3n0b31wxpzqzk">
                    {isEditingMaterials ? <input type="text" value={material.name} onChange={e => updateMaterial(index, 'name', e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded" data-id="element-1206" data-mp="3rxthg2uiayey" /> : <span className="text-stone-900" data-id="element-1207" data-mp="390ii6i8vk4zi">{material.name}</span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1208" data-mp="2va6o4dsqbw5q">
                    {isEditingMaterials ? <input type="number" step="0.01" value={material.price} onChange={e => updateMaterial(index, 'price', parseFloat(e.target.value) || 0)} className="w-24 px-2 py-1 border border-stone-200 rounded text-right" data-id="element-1209" data-mp="2z7umxrxjm92w" /> : <span className="text-stone-900" data-id="element-1210" data-mp="2blikgzuwyi1c">
                        kr {material.price.toFixed(2)}
                      </span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1211" data-mp="2ko0a7o9s1st3">
                    {isEditingMaterials ? <input type="number" value={material.quantity} onChange={e => updateMaterial(index, 'quantity', parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 border border-stone-200 rounded text-right" data-id="element-1212" data-mp="2zcebvvvgjb82" /> : <span className="text-stone-900" data-id="element-1213" data-mp="3ge0iofu9emm8">
                        {material.quantity}
                      </span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1214" data-mp="3q4dfp7j9g899">
                    <span className="text-stone-900 font-medium" data-id="element-1215" data-mp="3q54wvdc423g0">
                      kr {(material.price * material.quantity).toFixed(2)}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-1216" data-mp="7yqp5w4fqpq2">
              <tr className="border-t-2 border-stone-300 font-semibold" data-id="element-1217" data-mp="7y8hm3cbop2u">
                <td colSpan={3} className="py-4 px-4 text-stone-900" data-id="element-1218" data-mp="cvqto2lmzkf2">
                  Pris (Installation + boks)
                </td>
                <td className="py-4 px-4 text-right" data-id="element-1219" data-mp="29qws8xraqesn">
                  {isEditingMaterials ? <input type="number" step="0.01" value={installationPrice} onChange={e => setInstallationPrice(parseFloat(e.target.value) || 0)} className="w-32 px-2 py-1 border border-stone-200 rounded text-right font-semibold" data-id="element-1220" data-mp="2eoexoe63ubvw" /> : <span className="text-stone-900" data-id="element-1221" data-mp="1erms8exjy43t">
                      kr {installationPrice.toFixed(2)}
                    </span>}
                </td>
              </tr>
              <tr className="font-semibold" data-id="element-1222" data-mp="15qyd1gwf9xpc">
                <td colSpan={3} className="py-3 px-4 text-stone-900" data-id="element-1223" data-mp="15px5hmem7tcd">
                  Pris (Materialer)
                </td>
                <td className="py-3 px-4 text-right text-stone-900" data-id="element-1224" data-mp="10u7yiy09na61">
                  -kr {materialsCost.toFixed(2)}
                </td>
              </tr>
              <tr className="border-t-2 border-stone-300 font-bold text-lg" data-id="element-1225" data-mp="3lhb4n82ceaex">
                <td colSpan={3} className="py-4 px-4 text-emerald-700" data-id="element-1226" data-mp="1d94mn4swz3g0">
                  Profit pr stk.
                </td>
                <td className="py-4 px-4 text-right text-emerald-700" data-id="element-1227" data-mp="1i1691jjsqajr">
                  kr {chargerProfit.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>

      {/* Standard Materials Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm" data-id="element-1228" data-mp="1wxx117r54jzc">
        <div className="flex items-center gap-3 mb-6" data-id="element-1229" data-mp="1ryao6zf855r2">
          <div className="p-3 bg-teal-100 rounded-xl" data-id="element-1230" data-mp="1rwps35d9her3">
            <PackageIcon className="w-6 h-6 text-teal-700" data-id="element-1231" data-mp="bk180sl4ynbb" />
          </div>
          <div data-id="element-1232" data-mp="gjdqz28vza8m">
            <h2 className="text-lg font-semibold text-stone-900" data-id="element-1233" data-mp="gjx322buv4eq">
              Standard materialer
            </h2>
            <p className="text-sm text-stone-500" data-id="element-1234" data-mp="8ytihmhvqrys">
              Materialer der hurtigt kan vælges når der oprettes opgaver
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-8" data-id="element-1235" data-mp="i0at4pciq1g3">
          {standardMaterials.length === 0 ? <div className="p-6 text-center border border-stone-200 border-dashed rounded-xl bg-stone-50" data-id="element-1236" data-mp="d1gbhqqaeixk">
              <p className="text-sm text-stone-500" data-id="element-1237" data-mp="3lh020hzkpn8x">
                Ingen standard materialer endnu.
              </p>
            </div> : standardMaterials.map(material => <div key={material.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1238" data-mp="3v7mc5woitlk8">
                {editingMaterialId === material.id ? <div className="flex-1 flex items-center gap-3 mr-4" data-id="element-1239" data-mp="32p790nki8217">
                    <input type="text" value={editMaterialName} onChange={e => setEditMaterialName(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Materiale navn" data-id="element-1240" data-mp="2xsgu43kfrchd" />
                    <input type="number" step="0.01" value={editMaterialPrice} onChange={e => setEditMaterialPrice(e.target.value)} className="w-32 px-3 py-2 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Pris (valgfri)" data-id="element-1241" data-mp="1gdyhmoj1jly1" />
                  </div> : <div className="flex-1" data-id="element-1242" data-mp="wn4orssxr62d">
                    <p className="font-medium text-stone-900" data-id="element-1243" data-mp="241zm9md3mi86">
                      {material.name}
                    </p>
                    {material.price !== undefined && <p className="text-sm text-stone-500" data-id="element-1244" data-mp="3sn6pzb7p2egg">
                        {material.price.toLocaleString('da-DK')} kr
                      </p>}
                  </div>}

                <div className="flex items-center gap-2" data-id="element-1245" data-mp="3jtbm06ib6u2e">
                  {editingMaterialId === material.id ? <>
                      <button onClick={() => setEditingMaterialId(null)} className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-1246" data-mp="3et61daagi7tt">
                        <XIcon className="w-4 h-4" data-id="element-1247" data-mp="30uplasqpf4z0" />
                      </button>
                      <button onClick={() => handleSaveStandardMaterial(material.id)} className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1248" data-mp="2vwo4kegr3hqp">
                        <SaveIcon className="w-4 h-4" data-id="element-1249" data-mp="u5t1r2o4miah" />
                      </button>
                    </> : <>
                      <button onClick={() => startEditStandardMaterial(material)} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1250" data-mp="7z1pgfjqf0sw">
                        <EditIcon className="w-4 h-4" data-id="element-1251" data-mp="3uc9yw93iz26o" />
                      </button>
                      <button onClick={() => handleDeleteStandardMaterial(material.id)} className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1252" data-mp="3gdabf4rr0tc3">
                        <Trash2Icon className="w-4 h-4" data-id="element-1253" data-mp="3bhl94tocgyc3" />
                      </button>
                    </>}
                </div>
              </div>)}
        </div>

        <form onSubmit={handleAddStandardMaterial} className="p-5 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1254" data-mp="19m1dfbimgef9">
          <h3 className="text-sm font-semibold text-stone-900 mb-3" data-id="element-1255" data-mp="2p7v0nnqoeltb">
            Tilføj nyt standard materiale
          </h3>
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-1256" data-mp="33xj0sln4fefg">
            <input type="text" value={newMaterialName} onChange={e => setNewMaterialName(e.target.value)} placeholder="Materiale navn..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" required data-id="element-1257" data-mp="2z4pxm27awe5h" />
            <input type="number" step="0.01" value={newMaterialPrice} onChange={e => setNewMaterialPrice(e.target.value)} placeholder="Pris (valgfri)" className="w-full sm:w-40 px-4 py-2.5 rounded-lg border border-stone-200 focus:ring-2 focus:ring-teal-500 outline-none" data-id="element-1258" data-mp="3msratoffcir8" />
            <Button type="submit" variant="primary" className="flex items-center justify-center gap-2" data-id="element-1259" data-mp="3i5vvewze6xvj">
              <PlusIcon className="w-4 h-4" data-id="element-1260" data-mp="38h5l8xu783lg" />
              Tilføj
            </Button>
          </div>
        </form>
      </motion.div>
    </div>;
}